// トレイルチェックイン 共通処理（選手・スタッフ・管理画面・リザルトで共用）

// サーバー（Firebase・東京）の受付窓口のURL。
// URLの後ろに ?test を付けて開くと「試験モード」：この端末のブラウザの中だけで記録を保存して動く（合言葉は test）
const API_URL = /[?&]test\b/.test(location.search) ? '' : 'https://asia-northeast1-trail-checkin.cloudfunctions.net/api'; // 2026-10-21 Firebase（東京）へ引っ越し

// 各画面のURL（go-trail.net 上では /pages/…、手元では …html）
const PAGE_BASE = /go-trail\.net|myshopify\.com/.test(location.hostname) ? 'https://go-trail.net/pages/' : null;

// ---- アプリ（Capacitor）の中で動いているとき：位置情報をアプリの仕組みで取る ----
// ブラウザの位置情報を使うと、アプリの許可のあとに中のページ（localhost）からもう一度許可を聞かれてしまうため。
// navigator.geolocation と同じ使い方の代わりを差し込むので、各画面のコードはそのまま動く
(() => {
  const cap = window.Capacitor;
  if (!cap || !cap.isNativePlatform || !cap.isNativePlatform()) return;
  // 正式な呼び出し口（registerPlugin）を使う。Capacitor.Plugins 経由だとAndroidで位置の見張りが落ちることがある
  const reg = window.capacitorExports && window.capacitorExports.registerPlugin;
  const G = reg ? reg('Geolocation') : (cap.Plugins && cap.Plugins.Geolocation);
  if (!G) return;
  const toPos = p => ({ coords: p.coords, timestamp: p.timestamp });
  // 許可されていないときは code 1、それ以外（つかめない・時間切れ）は code 2/3（ブラウザと同じ番号）
  const toErr = async e => {
    let denied = false;
    try { denied = (await G.checkPermissions()).location === 'denied'; } catch (_) {}
    const msg = String((e && e.message) || e || '');
    return { code: denied || /denied|permission/i.test(msg) ? 1 : /timeout|time out/i.test(msg) ? 3 : 2, message: msg };
  };
  const shim = {
    getCurrentPosition(ok, ng, opts) {
      Promise.resolve(G.getCurrentPosition(opts || {})).then(p => ok(toPos(p)), async e => ng && ng(await toErr(e)));
    },
    watchPosition(ok, ng, opts) {
      let id = null;
      // 戻り値は Promise のことも、そのままの ID のこともあるので、どちらでも受ける
      Promise.resolve(G.watchPosition(opts || {}, async (p, e) => {
        if (p) ok(toPos(p)); else if (e && ng) ng(await toErr(e));
      })).then(x => { id = x; });
      return { clear: () => id && G.clearWatch({ id }) };
    },
    clearWatch(w) { if (w && w.clear) w.clear(); },
  };
  try { Object.defineProperty(navigator, 'geolocation', { value: shim, configurable: true }); } catch (_) {}
})();

const TF = (() => {
  const LS = {
    get(k, d) { try { const v = localStorage.getItem(k); return v == null ? d : JSON.parse(v); } catch (e) { return d; } },
    set(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); return true; } catch (e) { return false; } },
  };
  const uid = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
  const param = k => new URLSearchParams(location.search).get(k) || '';
  // この端末の目印（同じ番号が別のスマホで登録されたことに気づくため）
  const device = LS.get('tf_device', null) || (() => { const d = uid(); LS.set('tf_device', d); return d; })();
  const normBib = b => String(b ?? '').replace(/[０-９]/g, c => String.fromCharCode(c.charCodeAt(0) - 0xFEE0)).replace(/\s+/g, '');

  function pageUrl(page, eventId) {
    const files = { checkin: 'index.html', 'checkin-staff': 'staff.html', 'checkin-admin': 'admin.html', 'checkin-results': 'results.html' };
    const base = PAGE_BASE ? PAGE_BASE + page : new URL(files[page], location.href).href.split('?')[0];
    const q = new URLSearchParams();
    if (eventId) q.set('e', eventId);
    if (!API_URL) q.set('test', '');
    const s = q.toString().replace(/=(&|$)/g, '$1');
    return s ? `${base}?${s}` : base;
  }

  // timeout（ミリ秒）を付けると、電波が弱くて返事が来ないときに待ち続けない（SOSなど）
  // 主催者がログインしているときは、送るたびにログインの証明（IDトークン・1時間で自動更新）を添える
  let authFn = null;
  const setAuth = f => { authFn = f; };
  async function api(action, data = {}, opt = {}) {
    if (!API_URL) return mock(action, data);
    if (authFn && !data.idToken) { const t = await authFn(); if (t) data = { ...data, idToken: t }; }
    const ctl = opt.timeout && window.AbortController ? new AbortController() : null;
    const timer = ctl ? setTimeout(() => ctl.abort(), opt.timeout) : null;
    // text/plain で送ると事前確認(CORS)が発生せず、GASにそのまま届く
    // 返事の中身を読み終えるまでを時間切れの対象にする。アプリの通信方式（CapacitorHttp）では中断が効かないことがあるので、別に待って打ち切る
    const call = (async () => {
      const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify({ action, ...data }),
        signal: ctl ? ctl.signal : undefined,
      });
      // サーバーが混み合っている（503など）ときは、圏外と同じく送信待ちに残して送り直す
      if (!res.ok) throw new Error('サーバーが混み合っています。少し待ってから送り直します');
      return res.json();
    })();
    let raceTimer = null, json;
    try {
      json = await (opt.timeout
        ? Promise.race([call, new Promise((_, ng) => { raceTimer = setTimeout(() => ng(new Error('時間切れ')), opt.timeout); })])
        : call);
    } finally { if (timer) clearTimeout(timer); if (raceTimer) clearTimeout(raceTimer); }
    if (!json.ok) throw new ServerError(json.error || 'エラー');
    return json;
  }
  class ServerError extends Error {}

  // ---- 試験モード：GAS（gas/Code.gs）と同じ動きを端末内で再現 ----
  function mock(action, d) {
    const db = LS.get('tf_mock_db2', { events: [], records: [], roster: {} });
    db.roster ||= {};
    const saveDb = () => LS.set('tf_mock_db2', db);
    // 合言葉 test か、スタッフの招待リンク（s:招待の合言葉）
    // グループの招待リンク（g:合言葉）は、そのグループの大会だけ
    const gl = String(d.key).startsWith('g:') ? (db.groupLinks || {})[String(d.key).slice(2)] : null;
    const needKey = () => {
      if (gl) { if (d.eventId && (db.events.find(e => e.eventId === d.eventId) || {}).group !== gl) throw new ServerError('この大会を扱う権限がありません'); return; }
      if (d.key !== 'test' && !(String(d.key).startsWith('s:') && db.events.some(e => e.staffToken && 's:' + e.staffToken === d.key))) throw new ServerError('合言葉が違います');
    };
    const find = id => db.events.find(e => e.eventId === id);
    const must = id => { const e = find(id); if (!e) throw new ServerError('このイベントは見つかりません'); return e; };
    const summary = e => ({ eventId: e.eventId, title: e.title, date: e.date, status: e.status, publicResults: !!e.publicResults, regMode: e.regMode || 'auto', group: e.group || '' });
    const sorted = () => [...db.events].sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
    const write = ev => {
      if (!ev.cps || !ev.cps.length) throw new ServerError('チェックポイントがありません');
      if (!ev.eventId) { ev.eventId = Math.random().toString(36).slice(2, 8); ev.status = ev.status || 'open'; }
      if (!ev.staffToken) ev.staffToken = Math.random().toString(36).slice(2, 12) + Math.random().toString(36).slice(2, 8);
      ev.orgName = ev.orgName || 'お試し大会の主催者';
      const i = db.events.findIndex(e => e.eventId === ev.eventId);
      if (i >= 0) db.events[i] = ev; else db.events.push(ev);
      saveDb();
      return ev;
    };
    switch (action) {
      case 'getEvent': { const c = { ...must(d.eventId) }; delete c.sosEmails; return { ok: true, event: c }; }
      case 'record': {
        const r = d.record, ev = must(r.eventId);
        if (ev.status === 'closed' && r.type !== 'sos' && r.type !== 'sosloc' && (!r.at || !ev.closedAt || r.at > ev.closedAt)) throw new ServerError('このイベントは終了しています');
        if (!db.records.some(x => x.id === r.id)) {
          db.records.push({ ...r, photo: r.photo ? '(試験モードのため写真は保存しません)' : '', received: new Date().toISOString() });
          saveDb();
          if (r.type === 'sos') console.log('【試験モード】SOSメールを送ったことにします', r);
        }
        return { ok: true };
      }
      case 'register': {
        const ev = must(d.eventId);
        if (ev.status === 'closed') throw new ServerError('このイベントは終了しています');
        const regs = db.records.filter(r => r.eventId === ev.eventId && r.type === 'register');
        let bib, name, dup = false;
        if (ev.regMode === 'roster') {
          const ro = db.roster[ev.eventId] || [];
          const m = d.token ? ro.find(x => x.token === d.token) : ro.find(x => x.bib === normBib(d.bib));
          if (!m) throw new ServerError(d.token ? 'このQRコードは使えません' : 'このゼッケン番号は名簿にありません');
          bib = m.bib;
          name = m.name;
          d.cat = m.cat || '';
          dup = regs.some(r => r.bib === bib && r.device && r.device !== d.device);
        } else {
          name = String(d.name || '').trim();
          if (!name) throw new ServerError('お名前を入れてください');
          const mine = regs.find(r => r.device && r.device === d.device);
          bib = mine ? mine.bib : String(regs.reduce((mx, r) => Math.max(mx, +r.bib || 0), 0) + 1);
        }
        if (d.phone) { // 選手が入れた電話番号（名簿にない練習会の人は名簿に足す）
          const ro = db.roster[ev.eventId] ||= [];
          const m = ro.find(x => x.bib === bib);
          if (m) m.phone = d.phone; else ro.push({ bib, name, token: '', tag: '', phone: d.phone });
        }
        db.records.push({ id: uid(), type: 'register', eventId: ev.eventId, bib, name, device: d.device, at: new Date().toISOString() });
        saveDb();
        return { ok: true, bib, name, cat: d.cat || '', dup };
      }
      case 'lookupToken': {
        const ev = must(d.eventId);
        const m = (db.roster[ev.eventId] || []).find(x => x.token && x.token === d.token);
        if (!m) throw new ServerError('このQRコードは使えません。運営から届いた最新のQRコードか確かめてください');
        return { ok: true, bib: m.bib, name: m.name };
      }
      case 'lookupBib': {
        const ev = must(d.eventId);
        const m = (db.roster[ev.eventId] || []).find(x => x.bib === normBib(d.bib));
        if (!m) throw new ServerError('このゼッケン番号は名簿にありません。番号を確かめるか、スタッフに声をかけてください');
        return { ok: true, bib: m.bib, name: m.name };
      }
      case 'saveRoster': {
        needKey(); must(d.eventId);
        const seen = {}, old = {}, oldTag = {}, oldPhone = {};
        (db.roster[d.eventId] || []).forEach(m => { old[m.bib] = m.token; oldTag[m.bib] = m.tag || ''; oldPhone[m.bib] = m.phone || ''; });
        db.roster[d.eventId] = (d.roster || []).map(m => ({ bib: normBib(m.bib), name: String(m.name || '').trim(), phone: String(m.phone || '').trim(), cat: String(m.cat || '').trim() })).filter(m => m.bib && !seen[m.bib] && (seen[m.bib] = 1))
          .map(m => ({ ...m, token: old[m.bib] || Math.random().toString(36).slice(2, 12), tag: oldTag[m.bib] || '', phone: m.phone || oldPhone[m.bib] || '' }));
        saveDb();
        return { ok: true, roster: db.roster[d.eventId] };
      }
      case 'linkTag': {
        needKey(); must(d.eventId);
        const bib = normBib(d.bib), tag = String(d.tag || '').toUpperCase().replace(/[^0-9A-F]/g, '');
        if (!bib || !tag) throw new ServerError('ゼッケン番号とリストバンドの番号が必要です');
        const list = db.roster[d.eventId] = db.roster[d.eventId] || [];
        list.forEach(m => { if (m.tag === tag && m.bib !== bib) m.tag = ''; });
        let m = list.find(x => x.bib === bib);
        if (!m) list.push(m = { bib, name: String(d.name || '').trim(), token: Math.random().toString(36).slice(2, 12), tag: '' });
        m.tag = tag; if (!m.name && d.name) m.name = String(d.name).trim();
        saveDb();
        return { ok: true, member: { bib: m.bib, name: m.name, tag } };
      }
      case 'bandList': needKey(); return { ok: true, bands: db.bands || [] };
      case 'bandSet': case 'bandImport': {
        needKey();
        const list = db.bands = db.bands || [];
        const rows = d.rows || [{ uid: d.uid, no: d.no }];
        let added = 0;
        rows.forEach(r => {
          const uid = String(r.uid || '').toUpperCase().replace(/[^0-9A-F]/g, ''), no = normBib(r.no).replace(/^0+(?=\d)/, '');
          if (!uid || !no) return;
          const m = list.find(x => x.uid === uid);
          if (m) m.no = no; else { list.push({ uid, no }); added++; }
        });
        saveDb();
        return { ok: true, added, bands: list };
      }
      case 'contactAdd': {
        const list = db.contacts = db.contacts || [];
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(d.email || ''))) throw new ServerError('メールアドレスを確かめてください');
        list.push({ device: d.device, token: uid(), name: d.contactName, email: d.email, state: '有効', owner: d.owner }); // 試験モードは確認済みにする
        saveDb();
        return { ok: true, contacts: list.filter(c => c.device === d.device && c.state !== '削除') };
      }
      case 'contactList': return { ok: true, contacts: (db.contacts || []).filter(c => c.device === d.device && c.state !== '削除') };
      case 'contactRemove': {
        (db.contacts || []).forEach(c => { if (c.device === d.device && c.token === d.token) c.state = '削除'; });
        saveDb();
        return { ok: true, contacts: (db.contacts || []).filter(c => c.device === d.device && c.state !== '削除') };
      }
      case 'personalSos': {
        const to = (db.contacts || []).filter(c => c.device === d.device && c.state === '有効');
        if (!to.length) throw new ServerError('受け取りを承認した連絡先がありません');
        console.log('【試験モード】家族・知人にSOSメールを送ったことにします', d, to);
        return { ok: true, sent: to.length };
      }
      case 'feedback': {
        if (!String((d.feedback || {}).text || '').trim()) throw new ServerError('内容を書いてください');
        (db.feedback ||= []).push({ ...d.feedback, received: new Date().toISOString() });
        saveDb();
        console.log('【試験モード】ご意見を受け付けたことにします', d.feedback);
        return { ok: true };
      }
      case 'getResults': {
        const ev = find(d.eventId);
        if (!ev || !ev.publicResults) throw new ServerError('このイベントのリザルトは公開されていません');
        const runners = {};
        db.records.filter(r => r.eventId === ev.eventId && (r.type === 'checkin' || r.type === 'register'))
          .sort((a, b) => (a.at || '').localeCompare(b.at || '')).forEach(r => {
            const p = runners[r.bib] ||= { bib: r.bib, name: '', passes: {} };
            if (r.name) p.name = r.name;
            if (r.type === 'checkin' && !p.passes[r.cp]) p.passes[r.cp] = r.at;
          });
        const list = Object.values(runners);
        if (ev.regMode === 'roster') { const nm = {}; (db.roster[ev.eventId] || []).forEach(m => { nm[m.bib] = m.name; }); list.forEach(p => { if (nm[p.bib]) p.name = nm[p.bib]; }); }
        if (ev.showNames === false) list.forEach(p => { p.name = ''; });
        return { ok: true, event: { eventId: ev.eventId, title: ev.title, date: ev.date, startTime: ev.startTime || '', status: ev.status, cps: ev.cps.map(c => ({ id: c.id, name: c.name })) }, runners: list, updated: new Date().toISOString() };
      }
      case 'listResults': return { ok: true, events: sorted().filter(e => e.publicResults).map(summary) };
      case 'checkKey': needKey(); return { ok: true };
      case 'listEvents':
        needKey();
        return { ok: true, group: gl || '', events: sorted().filter(e => !gl || e.group === gl).map(e => ({ ...summary(e), runners: new Set(db.records.filter(r => r.eventId === e.eventId).map(r => r.bib)).size })) };
      case 'groupLink': {
        needKey();
        db.groupLinks ||= {};
        let t = Object.keys(db.groupLinks).find(k => db.groupLinks[k] === d.group);
        if (t && d.renew) { delete db.groupLinks[t]; t = ''; }
        if (!t) { t = Math.random().toString(36).slice(2, 10) + Math.random().toString(36).slice(2, 10); db.groupLinks[t] = d.group; saveDb(); }
        return { ok: true, token: t };
      }
      case 'getStatus': { needKey(); const ev = must(d.eventId); const st = slimStatus(db.records.filter(r => r.eventId === ev.eventId)); return { ok: true, event: ev, records: st.records, moves: st.moves, roster: db.roster[ev.eventId] || [], sos: db.sos || {} }; }
      case 'sosSet': {
        needKey(); must(d.eventId);
        db.sos ||= {};
        const v = db.sos[d.sosId] ||= { state: 'open' };
        const now = new Date().toISOString();
        v.state = d.state;
        if (d.state !== 'open' && !v.ackAt) { v.ackAt = now; v.ackBy = d.by || ''; }
        saveDb();
        return { ok: true, sos: v };
      }
      case 'sosStatus': {
        must(d.eventId);
        const has = db.records.some(r => r.id === d.sosId && r.type === 'sos');
        return { ok: true, ...(has ? (db.sos || {})[d.sosId] || { state: 'open' } : { state: 'none' }) };
      }
      case 'getRecords': { needKey(); const ev = must(d.eventId); return { ok: true, records: db.records.filter(r => r.eventId === ev.eventId) }; }
      case 'saveEvent': needKey(); return { ok: true, event: write(d.event) };
      case 'setEvent': {
        needKey();
        const ev = must(d.eventId);
        ['status', 'publicResults', 'showNames'].forEach(k => { if (k in d.changes) ev[k] = d.changes[k]; });
        if (d.changes.status === 'closed') ev.closedAt = new Date().toISOString();
        if (d.changes.status === 'open') delete ev.closedAt;
        if (d.changes.newStaffToken) ev.staffToken = '';
        return { ok: true, event: write(ev) };
      }
      default:
        throw new ServerError('不明な操作');
    }
  }

  // ---- 未送信の記録をためて、電波が戻ったら送る ----
  const Q = 'tf_queue';
  let flushing = false;
  const listeners = [];
  const queued = () => LS.get(Q, []);

  // サーバーが受け付けなかった記録（捨てずに残し、画面で「届かなかった」と出せるように）。新しい300件まで（写真は外して保存するので軽い）
  const REJ = 'tf_rejected';
  const rejected = () => LS.get(REJ, []);
  const reject = (rec, e) => {
    const error = String((e && e.message) || e || '');
    // 自分のSOSなら、その状態にも直接書いておく（一覧の保存に失敗しても「届いた」と出さないように）
    if (rec.type === 'sos' && rec.eventId) {
      const k = 'tf_mysos_' + rec.eventId, m = LS.get(k, null);
      if (m && m.id === rec.id) LS.set(k, { ...m, state: 'failed', error });
    }
    const list = rejected().filter(x => x.id !== rec.id);
    list.unshift({ id: rec.id, type: rec.type, at: new Date().toISOString(), error, rec: rec.photo ? { ...rec, photo: undefined } : rec });
    // 300件を超えたら古いものから外す。ただしSOSは残す
    const sos = list.filter(x => x.type === 'sos'), rest = list.filter(x => x.type !== 'sos');
    const keep = new Set([...sos, ...rest.slice(0, Math.max(0, 300 - sos.length))].map(x => x.id));
    return LS.set(REJ, list.filter(x => keep.has(x.id)));
  };
  const isRejected = id => rejected().some(x => x.id === id);
  // 送る順番：SOS → SOSの位置の続報 → チェックイン（写真なし）→ そのほか → 写真付き（重いので最後）
  const RANK = { sos: 0, sosloc: 1, checkin: 2, retire: 2, finish: 2 };
  const rank = r => r.photo ? 9 : (RANK[r.type] ?? 5);

  let again = false;
  // 圏外のとき、電波を探して送り続けると電池を使うので、失敗が続いたら間隔を空ける（15秒→30秒→…最大5分）
  let fails = 0, nextTry = 0;
  // 写真付きは重くて弱い電波だと送れないことが多いので、写真だけ別に間隔を空ける（30秒→60秒→…最大5分）
  let photoFails = 0, photoNext = 0;
  async function flush(force) {
    if (!force && Date.now() < nextTry) return;
    if (flushing) { if (force) again = true; return; } // 送信中に増えた記録は、終わったあとすぐ送る
    flushing = true;
    again = false;
    try {
      // 弱い電波で写真付きの記録が先頭に詰まり、SOSや現在地が送れなくなるのを防ぐため、軽くて急ぐものから送る。
      // 1件送るたびに選び直す（写真を送っている間に増えたSOSを、残りの写真より先に送る）
      const tried = new Set();
      const pick = () => queued().map((r, i) => ({ r, i }))
        .filter(x => !tried.has(x.r.id) && !(x.r.photo && Date.now() < photoNext))
        .sort((a, b) => rank(a.r) - rank(b.r) || a.i - b.i)[0];
      for (let x; (x = pick());) {
        const rec = x.r;
        tried.add(rec.id);
        try {
          await api('record', { record: rec }, { timeout: rec.photo ? 45000 : 15000 });
          if (rec.photo) photoFails = 0; else { fails = 0; nextTry = 0; }
          LS.set(Q, queued().filter(x => x.id !== rec.id));
        } catch (e) {
          if (e instanceof ServerError) {
            // サーバーが受け付けない記録は送り続けても無駄なので送信待ちから外す（記録は「届かなかった」として残す）
            console.warn('サーバーが受け付けなかった記録', rec, e);
            reject(rec, e);
            LS.set(Q, queued().filter(x => x.id !== rec.id));
            continue;
          }
          if (rec.photo) { photoFails++; photoNext = Date.now() + Math.min(15000 * 2 ** photoFails, 300000); continue; }
          fails++; nextTry = Date.now() + Math.min(15000 * 2 ** fails, 300000);
          break; // 圏外など。次の機会に送る
        }
      }
    } finally {
      flushing = false;
      listeners.forEach(f => f());
    }
    if (again) return flush(true);
  }

  // 送信待ちに入れるだけ（送るのは呼んだ側）。保存できなければ false
  function save(rec) {
    rec.id = rec.id || uid();
    const q = queued();
    if (!q.some(x => x.id === rec.id)) q.push(rec);
    if (!LS.set(Q, q)) return false;
    listeners.forEach(f => f());
    return true;
  }
  const drop = id => { LS.set(Q, queued().filter(x => x.id !== id)); listeners.forEach(f => f()); };

  // 記録をためてすぐ送信を試みる。保存できなければ false
  async function send(rec) {
    rec.id = rec.id || uid();
    const q = queued();
    q.push(rec);
    if (!LS.set(Q, q)) return false;
    listeners.forEach(f => f());
    await flush(true);
    return true;
  }

  window.addEventListener('online', () => flush(true));
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') flush(true); });
  setInterval(flush, 15000);

  // ---- 試験モード用：サーバー(gas/Code.gs)の slimStatus と同じ ----
  // 状況の画面に送る記録を軽くする：数分おきの現在地(pos)は各選手の最新1件だけにし、
  // かわりに「いつから同じ場所（100m以内）にいるか」(stillSince) を選手ごとに計算して添える
  function slimStatus(recs) {
    const R = 6371000, rad = Math.PI / 180;
    const dist = (a, b) => { const x = Math.pow(Math.sin((b.lat - a.lat) * rad / 2), 2) + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.pow(Math.sin((b.lon - a.lon) * rad / 2), 2); return 2 * R * Math.asin(Math.sqrt(x)); };
    const by = {}, out = [];
    recs.slice().sort((a, b) => String(a.at).localeCompare(String(b.at))).forEach(r => {
      const m = by[r.bib] = by[r.bib] || { last: null, anchor: null, stillSince: null };
      if (r.lat !== '' && r.lat != null) {
        const p = { lat: +r.lat, lon: +r.lon };
        if (!m.anchor || dist(m.anchor, p) > 100) { m.anchor = p; m.stillSince = r.at; }
      }
      if (r.type === 'pos') m.last = r; else out.push(r);
    });
    const moves = {};
    Object.keys(by).forEach(b => { if (by[b].last) out.push(by[b].last); moves[b] = { stillSince: by[b].stillSince }; });
    return { records: out, moves: moves };
  }

  // 見回り：min分以上 気になる人を3つに分けて出す。完走・リタイアの人は除く
  //   nosignal 通信なし ＝ 端末から何も届いていない（圏外・電池切れ・アプリが止まった）
  //   stale    位置が古い ＝ 通信は届いているが、GPSの位置が新しくならない（屋内・谷・GPSの不調）
  //   still    止まっているかも ＝ 新しい位置は届いているが、100m以内から動いていない（休憩・ケガ・道迷い）
  // 現在地を送る間隔が長い大会では、間隔の2.5倍までは「通信なし」にしない（20分ごとなら50分）
  function quietRunners(runners, moves = {}, min = 30, ev = null) {
    const now = Date.now(), lim = min * 60000, out = [];
    const sendLim = Math.max(lim, (+(ev && ev.posInterval) || 0) * 2.5 * 60000);
    const t = v => v ? new Date(v).getTime() : 0;
    runners.forEach(p => {
      if (p.status === 'done' || p.status === 'dnf' || p.status === 'sos') return; // SOSの人は、いちばん上のSOSの欄に出ている
      const m = moves[p.bib] || {};
      const seen = Math.max(t(m.seenAt), t(p.lastSeen)); // サーバーが最後に受け取った時刻（なければ最後の記録の時刻）
      if (!p.last) { if (p.started && now - seen >= sendLim) out.push({ p, kind: 'nosignal', since: m.seenAt || p.lastSeen || p.started.at }); return; }
      if (now - seen >= sendLim) { out.push({ p, kind: 'nosignal', since: new Date(seen).toISOString() }); return; }
      // 位置を測った時刻（送るときに、最後に測ってからの秒数 fixAge が付いている）
      const fix = t(p.last.at) - ((p.last.note && +p.last.note.fixAge) || 0) * 1000;
      if (now - fix >= sendLim) { out.push({ p, kind: 'stale', since: new Date(fix).toISOString() }); return; }
      const still = m.stillSince;
      if (still && now - t(still) >= lim) { out.push({ p, kind: 'still', since: still }); return; }
      // 電池が20%未満で充電していない人（選手のスマホにも通知が出ている）
      const charging = p.lastPos && p.lastPos.note && p.lastPos.note.charging;
      if (p.battery != null && p.battery < 20 && !charging) out.push({ p, kind: 'battery', since: p.lastPos.at });
    });
    const order = { nosignal: 0, stale: 1, still: 2, battery: 3 };
    return out.sort((a, b) => order[a.kind] - order[b.kind] || String(a.since).localeCompare(String(b.since)));
  }
  const QUIET = { nosignal: ['通信なし', 'danger'], stale: ['位置が古い', 'warn'], still: ['止まっているかも', 'warn'], battery: ['電池が少ない', 'warn'] };
  // 一覧のHTML（管理画面・スタッフ画面で共用）。地図のボタンは data-focus で受け取る
  function quietHtml(list, ev) {
    if (!list.length) return '<div class="sub">いません</div>';
    const row = ({ p, kind, since }) => {
      const mins = Math.round((Date.now() - new Date(since)) / 60000);
      const at = p.last;
      const cp = at && ev ? coursePos(ev, at.lat, at.lon, p.lastCp && p.lastCp.id) : null;
      return `<div style="padding:6px 0;border-bottom:1px solid var(--line)"><b>${esc(p.bib)} ${esc(p.name)}</b>　<span class="badge ${QUIET[kind][1]}">${kind === 'battery' ? `電池${p.battery}%` : `${QUIET[kind][0]} ${mins}分`}</span>${p.battery != null && kind !== 'battery' ? `　電池${p.battery}%` : ''}
        <div class="sub">${p.lastCp ? `最後の通過：${esc(p.lastCp.name)} ${fmtTime(p.lastCheck.at)}　` : ''}${cp ? esc(coursePosText(cp).split('\n').slice(0, 2).join('／')) : ''}${at ? `　<a href="#" data-focus="${at.lat},${at.lon}">地図で見る</a>` : ''}${p.phone ? '　' + telLink(p) : ''}</div></div>`;
    };
    return list.map(row).join('');
  }

  // ---- 状況の受け取り：はじめと30分に1回は全員分、あいだは「前回から変わった選手だけ」を受け取って足し合わせる ----
  // （800人の大会でも、15秒ごとに受け取るのは動いた数十人分だけ。古いサーバーなら毎回全員分の形で返ってくる）
  const newStatus = () => ({ eventId: '', map: {}, roster: [], since: 0, fullAt: 0 });
  const statusParams = (st, eventId) => {
    if (st.eventId !== eventId) Object.assign(st, newStatus(), { eventId });
    return st.since && Date.now() - st.fullAt < 1800000 ? { since: st.since } : {};
  };
  function applyStatus(st, r) {
    if (!r.runners) return { records: r.records || [], moves: r.moves || {}, roster: r.roster || [] };
    if (!r.partial) { st.map = {}; st.roster = r.roster || []; st.fullAt = Date.now(); }
    else (r.roster || []).forEach(m => { const i = st.roster.findIndex(x => x.bib === m.bib); if (i >= 0) st.roster[i] = m; else st.roster.push(m); });
    r.runners.forEach(u => { st.map[u.bib] = u; });
    st.since = r.now || Date.now(); // サーバーの時計で「ここまで受け取った」を覚える
    const records = [], moves = {};
    Object.values(st.map).forEach(u => { u.recs.forEach(x => records.push(x)); if (u.last) records.push(u.last); moves[u.bib] = { stillSince: u.stillSince, seenAt: u.seenAt }; });
    return { records, moves, roster: st.roster };
  }

  // ---- SOSの対応状況（サーバーの getStatus の sos）。open 未確認 → ack 確認済み → handling 対応中 → done 対応完了 ----
  let sosStates = {};
  const setSosStates = m => { sosStates = m || {}; };
  const SOS_STATE = { open: ['未確認', '#fff', '#c62828'], ack: ['確認済み', '#111', '#ffd000'], handling: ['対応中', '#111', '#ffd000'] };

  // ---- 選手ごとの状況（管理画面・スタッフ画面で共用） ----
  // ack = 対応済みにしたSOSのID一覧
  // roster = 名簿（レース）。名簿の名前を優先し、同じ番号を複数のスマホで登録していたら devices に出る
  function buildRunners(ev, records, ack = [], roster = []) {
    const byBib = {};
    const names = {};
    const phones = {};
    roster.forEach(m => { names[m.bib] = m.name; if (m.phone) phones[m.bib] = m.phone; });
    const sorted = [...records].sort((a, b) => (a.at || '').localeCompare(b.at || ''));
    for (const r of sorted) {
      const p = byBib[r.bib] ||= { bib: r.bib, name: '', checks: {}, last: null, sos: [], devices: [] };
      if (r.name) p.name = r.name;
      if (r.at) p.lastSeen = r.at;
      if (r.type === 'register' && r.device && !p.devices.includes(r.device)) p.devices.push(r.device);
      if (r.type === 'pos') p.lastPos = r; // 間隔ごとに届く現在地（電池残量つき）
      if (r.type === 'start') p.started = r;
      if (r.type === 'retire') p.retired = r;
      if (r.type === 'finish') p.finished = r;      // 選手が「ゴールした」を押した
      if (r.type === 'pause') p.paused = r;         // 一時停止中（再開で消える）
      if (r.type === 'resume' || r.type === 'start') p.paused = null;
      if (r.lat) p.last = r;
      if (r.type === 'checkin' && !p.checks[r.cp]) p.checks[r.cp] = r;
      if (r.type === 'sos') p.sos.push({ ...r });
      if (r.type === 'sosloc') { // 位置の続報：元のSOSの位置を新しくする
        const ref = r.note && r.note.ref;
        const sos = p.sos.find(x => x.id === ref) || p.sos[p.sos.length - 1];
        if (sos && r.lat) Object.assign(sos, { lat: r.lat, lon: r.lon, acc: r.acc, locAt: r.at });
      }
    }
    Object.values(byBib).forEach(p => { if (names[p.bib]) p.name = names[p.bib]; p.phone = phones[p.bib] || ''; });
    const now = new Date();
    const dueAt = cp => (ev.date && cp.due) ? new Date(`${ev.date}T${cp.due}:00`) : null;
    const order = { sos: 0, late: 1, run: 2, dnf: 3, done: 4 };
    return Object.values(byBib).map(p => {
      let lastIdx = -1;
      ev.cps.forEach((cp, i) => { if (p.checks[cp.id]) lastIdx = i; });
      const lastCp = lastIdx >= 0 ? ev.cps[lastIdx] : null;
      const next = ev.cps[lastIdx + 1] || null;
      // 対応完了（done）と、引っ越し前に閉じたSOS（closed）は出さない
      const openSos = p.sos.filter(s => !ack.includes(s.id) && !['done', 'closed'].includes((sosStates[s.id] || {}).state))
        .map(s => Object.assign(s, { state: 'open' }, sosStates[s.id] || {}));
      const due = next && dueAt(next);
      p.lastCp = lastCp; p.lastCheck = lastCp ? p.checks[lastCp.id] : null; p.next = next; p.openSos = openSos;
      p.status = openSos.length ? 'sos' : p.retired ? 'dnf' : (!next || p.finished) ? 'done' : (due && now > due) ? 'late' : 'run';
      const bat = p.lastPos && p.lastPos.note && p.lastPos.note.battery;
      p.battery = bat >= 0 ? bat : null;
      return p;
    }).sort((a, b) => order[a.status] - order[b.status] || String(a.bib).localeCompare(String(b.bib), 'ja', { numeric: true }));
  }
  const STATUS = { sos: ['SOS', 'danger'], late: ['遅れ', 'warn'], run: ['走行中', ''], dnf: ['リタイア', ''], done: ['完走', 'ok'] };
  const STATUS_COLOR = { sos: '#c62828', late: '#c27c0e', run: '#1976d2', dnf: '#777777', done: '#2e7d32' };
  // 状況の表の「最終通過」の下に出す、最後の現在地と電池（電池20%未満は目立たせる）
  const posLine = p => {
    if (!p.lastPos) return p.paused ? `<div class="sub"><span class="badge warn">${fmtTime(p.paused.at)}から一時停止中</span></div>` : '';
    const b = p.battery == null ? '' : `　<span class="${p.battery < 20 ? 'badge warn' : ''}">電池${p.battery}%</span>`;
    return `<div class="sub">📍 ${fmtTime(p.lastPos.at)} の位置${b}${p.paused ? `　<span class="badge warn">${fmtTime(p.paused.at)}から一時停止中</span>` : ''}</div>`;
  };

  // 対応済みにしたSOS（端末ごと）
  const ackKey = id => 'tf_sos_ack_' + id;
  const getAck = id => LS.get(ackKey(id), []);
  const addAck = (id, sosId) => { const a = getAck(id); a.push(sosId); LS.set(ackKey(id), a); };

  // ---- コース上の位置（gas/Code.gs と同じ計算） ----
  // SOSなどの位置を「コース上のどこか」に直す。afterCpId = その選手が最後に通過したポイント（8の字・往復で取り違えないため）
  function coursePos(ev, lat, lon, afterCpId) {
    const tr = (ev && ev.track) || [];
    if (tr.length < 2 || lat === '' || lat == null) return null;
    const R = 6371000, rad = Math.PI / 180;
    const d = (a, b, c, e) => { const x = Math.pow(Math.sin((c - a) * rad / 2), 2) + Math.cos(a * rad) * Math.cos(c * rad) * Math.pow(Math.sin((e - b) * rad / 2), 2); return 2 * R * Math.asin(Math.sqrt(x)); };
    const cum = [0];
    for (let i = 1; i < tr.length; i++) cum.push(cum[i - 1] + d(tr[i - 1][0], tr[i - 1][1], tr[i][0], tr[i][1]));
    // コースの線（点と点のあいだ）の上で一番近い所を探す。コースの点は間引いてある（最大1200点）ので、点だけで探すとずれを大きく見積もる
    const proj = (la, lo, i) => {
      const a = tr[i], b = tr[i + 1], kx = Math.cos(la * rad) * 111320, ky = 110540;
      const ax = (a[1] - lo) * kx, ay = (a[0] - la) * ky, vx = (b[1] - a[1]) * kx, vy = (b[0] - a[0]) * ky, l2 = vx * vx + vy * vy;
      const t = l2 ? Math.max(0, Math.min(1, -(ax * vx + ay * vy) / l2)) : 0;
      return { t, d: Math.hypot(ax + t * vx, ay + t * vy) };
    };
    // ほぼ同じ近さ（20m以内）なら、コースの手前の方を選ぶ（周回コースのスタート直後をゴール手前と取り違えない）
    const near = (la, lo, from) => {
      const f = Math.min(from, tr.length - 2), ps = [];
      for (let i = f; i < tr.length - 1; i++) ps.push(proj(la, lo, i));
      const best = Math.min(...ps.map(p => p.d)), k = ps.findIndex(p => p.d <= best + 20), i = f + k;
      return { i: i, d: ps[k].d, m: cum[i] + ps[k].t * (cum[i + 1] - cum[i]) };
    };
    // ポイントはコースの順番に、前のポイントより先で一番近い所に置く（往復で同じ場所を2回通っても取り違えない）
    let from = 0;
    const cpAt = ev.cps.map(cp => { const h = near(cp.lat, cp.lon, from); from = h.i; return { cp: cp, i: h.i, m: h.m }; });
    const after = cpAt.find(c => c.cp.id === afterCpId);
    const all = near(+lat, +lon, 0);
    let hit = after ? near(+lat, +lon, after.i) : all;
    if (hit.d > all.d + 200) hit = all; // 最後の通過より手前に戻っている（逆走・記録漏れ）ならコース全体で一番近い所
    const km = hit.m / 1000;
    let prev = null, next = null;
    cpAt.forEach(c => { if (c.m <= hit.m + 1) prev = c; else if (!next) next = c; });
    return {
      km: km, totalKm: cum[cum.length - 1] / 1000, off: Math.round(hit.d),
      prev: prev ? { name: prev.cp.name, km: km - prev.m / 1000 } : null,
      next: next ? { name: next.cp.name, km: next.m / 1000 - km } : null,
    };
  }
  
  function coursePosText(p) {
    if (!p) return '';
    const f = x => (Math.round(x * 10) / 10).toFixed(1);
    const lines = ['コース上 ' + f(p.km) + 'km地点（全長 ' + f(p.totalKm) + 'km）'];
    const seg = [];
    if (p.prev) seg.push(p.prev.name + ' から ' + f(p.prev.km) + 'km先');
    if (p.next) seg.push('次の ' + p.next.name + ' まで ' + f(p.next.km) + 'km');
    if (seg.length) lines.push(seg.join('／'));
    lines.push(p.off > 200 ? '⚠ コースから約' + p.off + 'm離れています（道迷いの可能性）' : 'コースからのずれ 約' + p.off + 'm');
    return lines.join('\n');
  }
  
  const gsiUrl = (lat, lon) => 'https://maps.gsi.go.jp/#16/' + lat + '/' + lon + '/&base=std&ls=std&disp=1&vs=c1g1j0h0k0l0u0t0z0r0s0m0f1';

  // ---- 地理計算・表示 ----
  function dist(a, b, c, d) {
    const R = 6371000, r = Math.PI / 180;
    const x = Math.sin((c - a) * r / 2) ** 2 + Math.cos(a * r) * Math.cos(c * r) * Math.sin((d - b) * r / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(x));
  }
  const fmtTime = iso => { if (!iso) return ''; const t = new Date(iso); return `${t.getHours()}:${String(t.getMinutes()).padStart(2, '0')}`; };
  // 緯度経度を「度分秒」で（119番などで聞かれたとき用）
  const toDms = (v, pos, neg) => {
    const a = Math.abs(v), d = Math.floor(a), mf = (a - d) * 60, m = Math.floor(mf), sec = ((mf - m) * 60).toFixed(1);
    return `${v >= 0 ? pos : neg}${d}度${m}分${sec}秒`;
  };
  const dms = (lat, lon) => `${toDms(lat, '北緯', '南緯')}　${toDms(lon, '東経', '西経')}`;
  const fmtDist = m => m >= 1000 ? `${(m / 1000).toFixed(1)}km` : `${Math.round(m)}m`;
  const fmtDur = ms => { if (!(ms >= 0)) return ''; const m = Math.floor(ms / 60000); return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`; };
  const esc = s => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const mapLink = r => r && r.lat ? `<a href="${gsiUrl(r.lat, r.lon)}" target="_blank" rel="noopener">地理院地図</a>　<a href="https://www.google.com/maps?q=${r.lat},${r.lon}" target="_blank" rel="noopener">Googleマップ</a>` : '';

  // SOSの帯（管理画面・スタッフ画面で共用）。地図のボタンは data-focus で受け取る
  // SOSの「確認した」「対応中」「対応完了」ボタンをつなぐ。押した人の名前は、その端末で1回だけ聞く
  function wireSos(box, eventId, key, after) {
    box.querySelectorAll('[data-sos]').forEach(b => b.onclick = async () => {
      const st = b.dataset.st;
      if (st === 'done' && !confirm('このSOSを「対応完了」にしますか？（全員の画面から消えます）')) return;
      let by = LS.get('tf_staff_name', '');
      if (!by) { by = (prompt('あなたの名前（だれが確認したか、ほかのスタッフと選手に伝わります）') || '').trim(); if (by) LS.set('tf_staff_name', by); }
      b.disabled = true; b.textContent = '送っています…';
      try {
        const r = await api('sosSet', { key, eventId, sosId: b.dataset.sos, state: st, by });
        sosStates[b.dataset.sos] = r.sos;
        after();
      } catch (e) { alert(e instanceof ServerError ? e.message : '送れませんでした。電波を確かめて、もう一度押してください'); b.disabled = false; }
    });
  }

  // 選手に電話するボタン（電話番号が分かっているときだけ）
  const telLink = (p, big) => p.phone ? `<a class="tel-btn${big ? ' big' : ''}" href="tel:${esc(p.phone.replace(/[^\d+]/g, ''))}">📞 電話する ${esc(p.phone)}</a>` : '';
  function sosHtml(ev, list) {
    return list.map(({ p, s }) => {
      const pos = s.lat ? coursePos(ev, s.lat, s.lon, p.lastCp && p.lastCp.id) : null;
      return `<div class="sos-item"><div><b>SOS　${esc(p.bib)} ${esc(p.name)}　${fmtTime(s.at)}</b>
        <div style="white-space:pre-line;font-weight:500">${s.lat ? esc(coursePosText(pos) || `${s.lat}, ${s.lon}`) + (s.locAt ? `\n（${fmtTime(s.locAt)} の続報の位置）` : '') : '位置不明' + (s.note && s.note.geoText ? `（${esc(s.note.geoText)}）` : '')}${p.lastCp ? `\n最後の通過：${esc(p.lastCp.name)} ${fmtTime(p.lastCheck.at)}` : ''}</div>
        <div style="font-weight:500">${s.lat ? `<a href="#" data-focus="${s.lat},${s.lon}">コース地図で見る</a>　` : ''}${mapLink(s)}</div>
        <div style="margin-top:6px">${telLink(p, true) || '<span style="font-weight:500">電話番号の登録なし</span>'}</div></div>
        <div style="display:flex;flex-direction:column;gap:6px;align-items:flex-end">
          <span style="background:${SOS_STATE[s.state || 'open'][2]};color:${SOS_STATE[s.state || 'open'][1]};border:2px solid #fff;border-radius:999px;padding:2px 12px;font-weight:700">${SOS_STATE[s.state || 'open'][0]}${s.ackAt ? `　${fmtTime(s.ackAt)}${s.ackBy ? ' ' + esc(s.ackBy) : ''}` : ''}</span>
          ${(s.state || 'open') === 'open' ? `<button data-sos="${esc(s.id)}" data-st="ack" style="padding:10px 18px;background:#fff;color:#c62828;font-weight:700">確認した</button>` : ''}
          ${s.state === 'ack' ? `<button class="ghost" data-sos="${esc(s.id)}" data-st="handling" style="padding:6px 12px">対応中にする</button>` : ''}
          ${s.state !== 'open' ? `<button class="ghost" data-sos="${esc(s.id)}" data-st="done" style="padding:6px 12px">対応完了</button>` : ''}
        </div></div>`;
    }).join('');
  }

  function baseMap(el) {
    const map = L.map(el, { zoomControl: true });
    L.tileLayer('https://cyberjapandata.gsi.go.jp/xyz/std/{z}/{x}/{y}.png', {
      maxZoom: 18, attribution: '<a href="https://maps.gsi.go.jp/development/ichiran.html">地理院タイル</a>',
    }).addTo(map);
    return map;
  }

  // コースとチェックポイントを描いた地図。戻り値の layer に選手の点を描く
  function courseMap(el, ev) {
    const map = baseMap(el);
    const line = L.polyline(ev.track || [], { color: '#e39a00', weight: 4, opacity: .8 }).addTo(map);
    ev.cps.forEach(cp => L.circleMarker([cp.lat, cp.lon], { radius: 6, color: '#e39a00', fillColor: '#fff', fillOpacity: 1, weight: 3 }).bindTooltip(cp.name).addTo(map));
    if (ev.track && ev.track.length) map.fitBounds(line.getBounds(), { padding: [16, 16] });
    else if (ev.cps[0]) map.setView([ev.cps[0].lat, ev.cps[0].lon], 13);
    return { map, layer: L.layerGroup().addTo(map) };
  }

  // 選手の最後の位置（SOS中ならSOSの位置）を地図に描く
  // opt.names=true でゼッケンに名前も添える。opt.ev を渡すと、吹き出しにコース上の位置・電池・最後の通過を出す
  // 30分以上前の位置（走行中・遅れ・SOSの人）は薄く表示し「○分前」を付ける。戻り値：ゼッケン→マーカー
  function drawRunners(layer, runners, opt = {}) {
    layer.clearLayers();
    const marks = {};
    const now = Date.now();
    runners.forEach(p => {
      const s = p.openSos.length ? p.openSos[p.openSos.length - 1] : null;
      const at = s && s.lat ? s : p.last;
      if (!at) return;
      const mins = Math.round((now - new Date(at.at)) / 60000);
      const stale = mins >= 30 && !['done', 'dnf'].includes(p.status);
      const label = `${p.bib}${opt.names && p.name ? ' ' + p.name : ''}${stale ? `（${mins}分前）` : ''}`;
      const m = L.circleMarker([at.lat, at.lon], {
        radius: p.status === 'sos' ? 11 : 8, color: '#fff', weight: 2, fillColor: STATUS_COLOR[p.status],
        fillOpacity: stale ? 0.35 : 1, dashArray: stale ? '3 3' : null,
      }).bindTooltip(esc(label), { permanent: true, direction: 'right', offset: [8, 0], className: p.status === 'sos' ? 'tip-sos' : '' });
      let html = `<b>${esc(p.bib)} ${esc(p.name)}</b>　${STATUS[p.status][0]}<br>${fmtTime(at.at)} の位置（${mins < 1 ? 'たった今' : mins + '分前'}）`;
      if (p.battery != null) html += `　電池${p.battery}%`;
      if (p.lastCp) html += `<br>最後の通過：${esc(p.lastCp.name)} ${fmtTime(p.lastCheck.at)}`;
      if (opt.ev) { const cp = coursePos(opt.ev, at.lat, at.lon, p.lastCp && p.lastCp.id); if (cp) html += `<br>${esc(coursePosText(cp)).replace(/\n/g, '<br>')}`; }
      html += `<br>${mapLink(at)}`;
      m.bindPopup(html).addTo(layer);
      marks[p.bib] = m;
    });
    return marks;
  }


  // ---- ご意見・不具合を送る（どの画面からも使う） ----
  // ctx = { page: '選手画面' など, eventId, bib, name }
  function feedback(ctx = {}) {
    let m = document.getElementById('tf-fb');
    if (!m) {
      m = document.createElement('div');
      m.id = 'tf-fb'; m.className = 'modal hidden';
      document.body.appendChild(m);
    }
    m.innerHTML = `<div class="card"><h2>ご意見・不具合を送る</h2>
      <p class="sub" style="margin:0 0 8px">いただいた声は、すべて読んで改善に使います。個別のお返事はできないことがあります。</p>
      <label>種類</label>
      <select id="tf-fb-kind"><option value="bug">不具合（うまく動かない）</option><option value="idea">ほしい機能・使いにくい所</option><option value="other">その他</option></select>
      <label>内容</label>
      <textarea id="tf-fb-text" rows="5" placeholder="例：CP2でチェックインを押したら「位置がわかりません」と出た"></textarea>
      <label>連絡先（任意・お返事がほしい場合）</label>
      <input id="tf-fb-contact" placeholder="メールアドレスなど">
      <div class="sub" style="margin-top:6px">スマホの機種・OS・この画面の情報も一緒に送られます。</div>
      <div id="tf-fb-msg"></div>
      <div class="row" style="margin-top:12px"><button id="tf-fb-send" style="flex:1">送る</button><button class="ghost" id="tf-fb-close">閉じる</button></div></div>`;
    m.classList.remove('hidden');
    m.querySelector('#tf-fb-close').onclick = () => m.classList.add('hidden');
    m.querySelector('#tf-fb-send').onclick = async () => {
      const btn = m.querySelector('#tf-fb-send'), msg = m.querySelector('#tf-fb-msg');
      const text = m.querySelector('#tf-fb-text').value.trim();
      if (!text) { msg.innerHTML = '<div class="banner warn">内容を書いてください</div>'; return; }
      btn.disabled = true; btn.textContent = '送っています…';
      const env = [navigator.userAgent.match(/iPhone OS [\d_]+|iPad|Android [\d.]+[^;)]*|Mac OS X [\d_]+|Windows NT [\d.]+/)?.[0] || '',
        /CriOS|Chrome/.test(navigator.userAgent) ? 'Chrome' : /Safari/.test(navigator.userAgent) ? 'Safari' : '',
        `${screen.width}x${screen.height}`, navigator.onLine ? 'オンライン' : 'オフライン', `未送信${queued().length}件`].filter(Boolean).join(' / ');
      try {
        await api('feedback', { feedback: { ...ctx, kind: m.querySelector('#tf-fb-kind').value, text, contact: m.querySelector('#tf-fb-contact').value.trim(), device, env, url: location.href.split('#')[0] } });
        m.querySelector('.card').innerHTML = '<h2>ありがとうございました</h2><p>いただいた声は、改善に使わせていただきます。</p><button class="big" onclick="document.getElementById(\'tf-fb\').classList.add(\'hidden\')">閉じる</button>';
      } catch (e) {
        btn.disabled = false; btn.textContent = '送る';
        msg.innerHTML = `<div class="banner warn">${esc(e instanceof ServerError ? e.message : '送れませんでした。電波のある所でもう一度お試しください')}</div>`;
      }
    };
  }

  return {
    feedback,
    LS, uid, param, device, normBib, pageUrl, api, setAuth, send, save, drop, flush, queued, rejected, reject, isRejected, onQueueChange: f => listeners.push(f),
    buildRunners, STATUS, STATUS_COLOR, posLine, quietRunners, quietHtml, getAck, addAck,
    coursePos, coursePosText, gsiUrl, sosHtml, telLink, wireSos, setSosStates, newStatus, statusParams, applyStatus, dms, dist, fmtTime, fmtDist, fmtDur, esc, mapLink, baseMap, courseMap, drawRunners, testMode: !API_URL, ServerError,
  };
})();

// アプリでは「戻る」ボタンもスワイプ戻りもないので、リザルト・スタッフ画面の上に選手画面へ戻る道を出す（Web版はブラウザで戻れるので出さない）
(() => {
  const cap = window.Capacitor;
  if (!cap || !cap.isNativePlatform || !cap.isNativePlatform()) return;
  if (!/(results|staff)\.html$/.test(location.pathname)) return;
  const add = () => {
    const wrap = document.querySelector('.wrap');
    if (!wrap || document.getElementById('tf-back')) return;
    const a = document.createElement('a');
    a.id = 'tf-back'; const last = TF.LS.get('tf_last_event', null); a.href = TF.pageUrl('checkin', last && last.id); a.textContent = '← 選手画面へ戻る';
    a.style.cssText = 'display:inline-block;margin:0 0 8px;padding:8px 0;font-size:16px';
    wrap.prepend(a);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', add); else add();
})();
